// App.tsx (updated with Framer Motion for hero fades and route transitions)
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaGithub, FaLinkedin } from 'react-icons/fa';
import Portfolio from './components/Portfolio';
import TicketingDashboard from './components/TicketingDashboard';
import SalesDashboard from './components/SalesDashboard';
import RagAgent from './components/RagAgent';
import StockMarketTracker from './components/StockMarketTracker';
import CustomerSegmentation from './components/CustomerSegmentation';
import YouTubeSentimentAnalyzer from './components/YouTubeSentimentAnalyzer';
import WeatherForecastApp from './components/WeatherForecastApp';
import { ThemeProvider, useTheme } from './context/ThemeContext';

interface FormData {
  name: string;
  email: string;
  projectType: string;
  budget: string;
  message: string;
  company: string; // honeypot
}

// Theme Toggle Component
const ThemeToggle: React.FC = () => {
  const { theme, setTheme } = useTheme();
  return (
    <select
      value={theme}
      onChange={(e) => setTheme(e.target.value as any)}
      className="ml-4 px-3 py-1 border border-primary rounded-md bg-secondary text-primary focus:ring-2 focus:ring-accent"
    >
      <option value="light">Light</option>
      <option value="dark">Dark</option>
    </select>
  );
};

const AnimatedRoutes: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        {children}
      </Routes>
    </AnimatePresence>
  );
};

const AnimatedRouteElement: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <motion.div
    initial={{ opacity: 0, x: 50 }}
    animate={{ opacity: 1, x: 0 }}
    exit={{ opacity: 0, x: -50 }}
    transition={{ duration: 0.3 }}
  >
    {children}
  </motion.div>
);

// Stats strip for social proof
const Stats: React.FC = () => {
  const stats = [
    { value: '10+', label: 'Years in tech' },
    { value: '50+', label: 'Projects shipped' },
    { value: '24h', label: 'Avg. response time' },
    { value: '100%', label: 'Owner-operated' },
  ];
  return (
    <section className="py-12 bg-secondary border-y border-primary/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {stats.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="text-4xl font-bold text-accent mb-1">{s.value}</div>
              <div className="text-sm text-secondary uppercase tracking-wider">{s.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// How we work together — packages for individual clients
const Packages: React.FC = () => {
  const packages = [
    {
      name: 'Quick Build',
      tagline: 'For a focused, one-off need',
      points: ['Single script, tool, or landing page', 'Scoped in a short call', 'Fast turnaround', 'Fixed price, no surprises'],
      cta: 'Start small',
    },
    {
      name: 'Project Sprint',
      tagline: 'For a complete app or model',
      points: ['Full-stack app, dashboard, or ML model', 'Design → build → deploy', 'Weekly check-ins', 'Production-ready handoff'],
      cta: 'Build it right',
      featured: true,
    },
    {
      name: 'Ongoing Partner',
      tagline: 'For evolving products',
      points: ['Retained availability', 'Iterate, maintain, and scale', 'AI/data strategy advice', 'Priority response'],
      cta: 'Work together',
    },
  ];
  return (
    <section id="work-with-me" className="py-20 bg-secondary">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-bold text-center mb-3 text-primary">Ways to Work Together</h2>
        <p className="text-center text-secondary mb-12 max-w-2xl mx-auto">
          Whether you need one small thing built or a full product, there's a straightforward way to get started. No agencies, no middlemen — you work directly with me.
        </p>
        <div className="grid md:grid-cols-3 gap-8">
          {packages.map((pkg, i) => (
            <motion.div
              key={pkg.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              viewport={{ once: true }}
              className={`p-8 rounded-xl border transition-all duration-300 flex flex-col ${
                pkg.featured
                  ? 'border-accent shadow-2xl scale-105 bg-primary ring-2 ring-accent/40'
                  : 'border-primary bg-primary shadow-md hover:shadow-xl'
              }`}
            >
              {pkg.featured && (
                <span className="self-start mb-4 text-xs font-bold uppercase tracking-wider accent-bg text-white px-3 py-1 rounded-full">
                  Most Popular
                </span>
              )}
              <h3 className="text-2xl font-bold text-primary mb-1">{pkg.name}</h3>
              <p className="text-secondary text-sm mb-6">{pkg.tagline}</p>
              <ul className="space-y-3 mb-8 flex-1">
                {pkg.points.map((pt) => (
                  <li key={pt} className="flex items-start text-secondary">
                    <span className="text-accent mr-2 font-bold">✓</span>
                    <span>{pt}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/#contact"
                className={`text-center px-6 py-3 rounded-full font-semibold transition ${
                  pkg.featured ? 'accent-bg text-white hover:opacity-90' : 'border border-primary text-primary hover:border-accent'
                }`}
              >
                {pkg.cta}
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Simple process section — reduces perceived risk for individuals
const Process: React.FC = () => {
  const steps = [
    { n: '1', title: 'Reach out', desc: 'Tell me what you need — even a rough idea works.' },
    { n: '2', title: 'Quick plan', desc: 'I scope it, give a clear price and timeline. No obligation.' },
    { n: '3', title: 'I build it', desc: 'You get regular updates. I handle the technical heavy lifting.' },
    { n: '4', title: 'You ship', desc: 'Deployed, documented, and yours. Support if you need it.' },
  ];
  return (
    <section className="py-20 bg-primary">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-bold text-center mb-12 text-primary">How It Works</h2>
        <div className="grid md:grid-cols-4 gap-8">
          {steps.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="w-14 h-14 mx-auto mb-4 rounded-full accent-bg text-white flex items-center justify-center text-2xl font-bold">
                {s.n}
              </div>
              <h3 className="text-xl font-semibold text-primary mb-2">{s.title}</h3>
              <p className="text-secondary text-sm">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Home: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    projectType: '',
    budget: '',
    message: '',
    company: '',
  });
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      setTimeout(() => {
        const id = location.hash.substring(1);
        const element = document.getElementById(id);
        if (element) {
          const yOffset = -80; // Adjust for fixed navigation height
          const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 100);
    }
  }, [location.hash, location.key]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Honeypot: bots fill hidden field
    if (formData.company) {
      setSubmitted(true);
      return;
    }

    setIsLoading(true);
    setError('');
    setSubmitted(false);

    try {
      const composedMessage =
        `Project type: ${formData.projectType || 'Not specified'}\n` +
        `Budget: ${formData.budget || 'Not specified'}\n\n` +
        `${formData.message}`;

      const response = await fetch('https://matthew-daggs-backend-136257652789.us-central1.run.app/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: formData.name, email: formData.email, message: composedMessage }),
      });
      if (!response.ok) {
        throw new Error('Submission failed');
      }

      await response.json();
      setSubmitted(true);
      setFormData({ name: '', email: '', projectType: '', budget: '', message: '', company: '' });
    } catch (err) {
      setError('Failed to send message. Please try again or email me directly.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* Hero Section with fade animation */}
      <motion.section
        id="home"
        className="pt-28 pb-24 bg-gradient-to-r from-accent to-purple-700 text-white"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.span
            className="inline-block mb-6 px-4 py-1.5 rounded-full bg-white/15 text-sm font-semibold backdrop-blur-sm"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            ● Available for new projects
          </motion.span>
          <h2 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
            Need something built? <br className="hidden md:block" />I'll ship it.
          </h2>
          <p className="text-lg md:text-xl mb-10 max-w-3xl mx-auto opacity-95">
            Freelance data scientist, AI engineer, and full-stack developer. From a quick one-off tool to a full production app — you work directly with me, and I turn your idea into working software.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/#contact" className="bg-white text-accent px-8 py-3 rounded-full font-bold hover:opacity-90 transition shadow-lg">
              Start a Project
            </Link>
            <Link to="/portfolio" className="border-2 border-white/70 text-white px-8 py-3 rounded-full font-semibold hover:bg-white/10 transition">
              See My Work
            </Link>
          </div>
        </div>
      </motion.section>

      {/* Social proof stats */}
      <Stats />

      {/* Services Section */}
      <section id="services" className="py-20 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12 text-primary">What I Can Build For You</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6 border border-primary rounded-lg shadow-md hover:shadow-lg transition bg-secondary">
              <h3 className="text-2xl font-semibold mb-4 text-primary">Data Science</h3>
              <ul className="text-left mb-6">
                <li>• Predictive modeling & analytics</li>
                <li>• Data visualization & dashboards</li>
                <li>• Machine learning pipelines</li>
              </ul>
              <p className="text-secondary">Transform raw data into actionable insights.</p>
            </div>
            <div className="text-center p-6 border border-primary rounded-lg shadow-md hover:shadow-lg transition bg-secondary">
              <h3 className="text-2xl font-semibold mb-4 text-primary">Generative AI</h3>
              <ul className="text-left mb-6">
                <li>• Custom LLM fine-tuning</li>
                <li>• AI chatbots & content generation</li>
                <li>• Image/video synthesis</li>
              </ul>
              <p className="text-secondary">Harness the power of AI to create and innovate.</p>
            </div>
            <div className="text-center p-6 border border-primary rounded-lg shadow-md hover:shadow-lg transition bg-secondary">
              <h3 className="text-2xl font-semibold mb-4 text-primary">Web Development</h3>
              <ul className="text-left mb-6">
                <li>• Full-stack apps (React/Node.js)</li>
                <li>• E-commerce & CMS sites</li>
                <li>• Responsive UI/UX design</li>
              </ul>
              <p className="text-secondary">Craft modern, scalable web experiences.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Ways to work together */}
      <Packages />

      {/* How it works */}
      <Process />

      {/* About Section */}
      <section id="about" className="py-20 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2 
            className="text-3xl font-bold text-center mb-12 text-primary"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            About Me
          </motion.h2>
          <div className="flex flex-col md:flex-row items-center gap-12">
            <motion.img 
              src="/md-image.jpeg"
              alt="Profile" 
              className="w-64 h-64 rounded-full object-cover mx-auto md:mx-0 shadow-lg ring-4 ring-primary/20" 
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            />
            <div className="md:flex-1 max-w-2xl mx-auto md:mx-0">
              <motion.h3 
                className="text-2xl font-semibold mb-4 text-primary text-center md:text-left"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                Matthew Daggs | Principal Data Scientist & Software Architect
              </motion.h3>
              <motion.p 
                className="text-secondary mb-6 leading-relaxed text-center md:text-left"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                viewport={{ once: true }}
              >
                With 10+ years in tech, I blend data science, AI, and web dev to solve complex problems. Passionate about ethical AI and user-centric design. Let's collaborate to turn your vision into reality.
              </motion.p>
              <motion.div 
                className="flex justify-center md:justify-start space-x-4 mb-8"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
              >
                <a href="https://github.com/mdaggs" className="text-secondary hover:text-primary transition-colors duration-300" aria-label="GitHub Profile">
                  {(FaGithub as any)({ size: 24 })}
                </a>
                <a href="https://www.linkedin.com/in/matthew-daggs-4870b47a/" className="text-secondary hover:text-primary transition-colors duration-300" aria-label="LinkedIn Profile">
                  {(FaLinkedin as any)({ size: 24 })}
                </a>
              </motion.div>
              <motion.div
                className="p-6 bg-secondary rounded-lg shadow-md border border-primary/50"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                viewport={{ once: true }}
              >
                <h4 className="font-semibold text-primary mb-4 text-xl">Core Expertise</h4>
                <p className="text-secondary mb-6 leading-relaxed">
                  As a Principal Data Scientist and Software Architect with over a decade of leadership in high-stakes environments, I architect end-to-end AI-driven systems that scale from prototype to production, delivering measurable business impact through ethical, innovative solutions. My toolkit spans:
                </p>
                <ul className="space-y-4 mb-6 text-secondary">
                  <li className="font-medium leading-relaxed">
                    <strong className="text-primary block mb-1">Advanced Machine Learning & Generative AI</strong>
                    Expertise in TensorFlow, PyTorch, and Hugging Face for building and fine-tuning large language models (LLMs), diffusion models, and reinforcement learning agents; proficient in deploying production-grade pipelines that handle petabyte-scale datasets.
                  </li>
                  <li className="font-medium leading-relaxed">
                    <strong className="text-primary block mb-1">Data Engineering & Analytics</strong>
                    Masterful command of Python (with libraries like Pandas) for designing resilient ETL pipelines, coupled with Spark or BigQuery for real-time streaming and big data orchestration; skilled in feature engineering, anomaly detection, and causal inference to unlock predictive insights.
                  </li>
                  <li className="font-medium leading-relaxed">
                    <strong className="text-primary block mb-1">Full-Stack Development & Cloud Architecture</strong>
                    Architect scalable microservices using React, and Node.js/Express, integrated with GraphQL and serverless paradigms; deep experience in GCP (Vertex AI, Cloud Run) for secure, cost-optimized infrastructures supporting CI/CD via GitHub Actions and Terraform.
                  </li>
                  <li className="font-medium leading-relaxed">
                    <strong className="text-primary block mb-1">Database & Data Systems</strong>
                    Advanced SQL/NoSQL optimization (PostgreSQL, MongoDB, Cassandra) for querying complex schemas; expertise in vector databases like Chroma, Vertex AI Search, Pinecone and FAISS for semantic search in RAG systems, ensuring low-latency access to unstructured data at enterprise scale.
                  </li>
                  <li className="font-medium leading-relaxed">
                    <strong className="text-primary block mb-1">Domain-Specific Innovations</strong>
                    Proven track record in ethical AI governance, federated learning for privacy-preserving models, and interdisciplinary applications—from financial forecasting with time-series ARIMA/LSTM hybrids to NLP-driven sentiment analysis and computer vision for autonomous systems.
                  </li>
                </ul>
                <p className="text-secondary italic leading-relaxed">
                  I thrive at the intersection of data, AI, and software engineering, mentoring teams to deliver transformative solutions that drive revenue, efficiency, and competitive advantage.
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12 text-primary">Let's Build Something</h2>
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-semibold mb-6 text-primary">Ready to Start Your Project?</h3>
              <p className="text-secondary mb-6">
                Tell me what you have in mind — even a rough idea is enough. I'll reply within 24 hours with next steps, and there's no obligation to move forward.
              </p>
              <div className="space-y-3 text-secondary">
                <p>
                  <strong className="text-primary">Prefer email?</strong>{' '}
                  <a href="mailto:hello@matthewdaggs.com" className="text-accent hover:underline">hello@matthewdaggs.com</a>
                </p>
                <p className="text-sm">No project too small — I regularly build one-off tools and scripts for individuals.</p>
              </div>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-secondary mb-1">Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full p-3 border border-primary rounded-md focus:ring-2 focus:ring-accent focus:border-transparent bg-primary text-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary mb-1">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full p-3 border border-primary rounded-md focus:ring-2 focus:ring-accent focus:border-transparent bg-primary text-primary"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-secondary mb-1">Project type</label>
                  <select
                    name="projectType"
                    value={formData.projectType}
                    onChange={handleChange}
                    className="w-full p-3 border border-primary rounded-md focus:ring-2 focus:ring-accent focus:border-transparent bg-primary text-primary"
                  >
                    <option value="">Select…</option>
                    <option value="Quick one-off build">Quick one-off build</option>
                    <option value="Full app / product">Full app / product</option>
                    <option value="Data / ML / AI">Data / ML / AI</option>
                    <option value="Ongoing work">Ongoing work</option>
                    <option value="Not sure yet">Not sure yet</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary mb-1">Budget (optional)</label>
                  <select
                    name="budget"
                    value={formData.budget}
                    onChange={handleChange}
                    className="w-full p-3 border border-primary rounded-md focus:ring-2 focus:ring-accent focus:border-transparent bg-primary text-primary"
                  >
                    <option value="">Select…</option>
                    <option value="Under $1k">Under $1k</option>
                    <option value="$1k–$5k">$1k–$5k</option>
                    <option value="$5k–$15k">$5k–$15k</option>
                    <option value="$15k+">$15k+</option>
                    <option value="Not sure">Not sure</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary mb-1">Message</label>
                <textarea
                  name="message"
                  rows={5}
                  value={formData.message}
                  onChange={handleChange}
                  required
                  className="w-full p-3 border border-primary rounded-md focus:ring-2 focus:ring-accent focus:border-transparent bg-primary text-primary"
                  placeholder="Tell me about your project..."
                ></textarea>
              </div>
              {/* Honeypot: hidden from users, catches bots */}
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                tabIndex={-1}
                autoComplete="off"
                className="hidden"
                aria-hidden="true"
              />
              <button
                type="submit"
                disabled={isLoading}
                className="w-full accent-bg text-white py-3 rounded-md font-semibold hover:opacity-90 transition disabled:opacity-50"
              >
                {isLoading ? 'Sending...' : 'Send Message'}
              </button>
              {submitted && <p className="text-green-600 text-center">Thanks! I'll get back to you within 24 hours.</p>}
              {error && <p className="text-red-500 text-center">{error}</p>}
            </form>
          </div>
        </div>
      </section>
    </>
  );
};

const App: React.FC = () => {
  const [menuOpen, setMenuOpen] = useState<boolean>(false);

  const navLinks = [
    { to: '/', label: 'Home', aria: 'Go to Home section' },
    { to: '/#services', label: 'Services', aria: 'Go to Services section' },
    { to: '/#work-with-me', label: 'Hire Me', aria: 'Go to Work With Me section' },
    { to: '/portfolio', label: 'Portfolio', aria: 'Go to Portfolio section' },
    { to: '/#about', label: 'About', aria: 'Go to About section' },
    { to: '/#contact', label: 'Contact', aria: 'Go to Contact section' },
  ];

  return (
    <ThemeProvider>
      <Router>
        <div className="min-h-screen font-sans">
          {/* Navigation */}
          <nav className="bg-primary shadow-lg fixed w-full z-50 border-b border-primary">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between h-16">
                <div className="flex items-center">
                  <h1 className="text-xl font-bold text-primary">
                    <Link to="/" onClick={() => setMenuOpen(false)}>Matthew Daggs</Link>
                  </h1>
                </div>
                {/* Desktop links */}
                <div className="hidden md:flex space-x-8 items-center">
                  {navLinks.map((l) => (
                    <Link key={l.to} to={l.to} className="text-secondary hover:text-primary" aria-label={l.aria}>
                      {l.label}
                    </Link>
                  ))}
                  <ThemeToggle />
                </div>
                {/* Mobile controls */}
                <div className="flex md:hidden items-center">
                  <ThemeToggle />
                  <button
                    type="button"
                    onClick={() => setMenuOpen((o) => !o)}
                    className="ml-4 px-3 py-1 text-2xl text-primary"
                    aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                    aria-expanded={menuOpen}
                  >
                    {menuOpen ? '✕' : '☰'}
                  </button>
                </div>
              </div>
            </div>
            {menuOpen && (
              <div className="md:hidden border-t border-primary bg-primary px-4 pb-4">
                {navLinks.map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    onClick={() => setMenuOpen(false)}
                    className="block py-3 text-secondary hover:text-primary border-b border-primary/50"
                    aria-label={l.aria}
                  >
                    {l.label}
                  </Link>
                ))}
              </div>
            )}
          </nav>

          {/* Routes */}
          <AnimatedRoutes>
            <Route
              path="/"
              element={
                <AnimatedRouteElement>
                  <Home />
                </AnimatedRouteElement>
              }
            />
            <Route path="/portfolio" element={<AnimatedRouteElement><Portfolio /></AnimatedRouteElement>} />
            <Route path="/portfolio/ticketing-dashboard" element={<AnimatedRouteElement><TicketingDashboard /></AnimatedRouteElement>} />
            <Route path="/portfolio/sales-dashboard" element={<AnimatedRouteElement><SalesDashboard /></AnimatedRouteElement>} />
            <Route path="/portfolio/rag-agent" element={<AnimatedRouteElement><RagAgent /></AnimatedRouteElement>} />
            <Route path="/portfolio/stock-market-tracker" element={<AnimatedRouteElement><StockMarketTracker /></AnimatedRouteElement>} />
            <Route path="/portfolio/customer-segmentation" element={<AnimatedRouteElement><CustomerSegmentation /></AnimatedRouteElement>} />
            <Route path="/portfolio/youtube-sentiment-analyzer" element={<AnimatedRouteElement><YouTubeSentimentAnalyzer /></AnimatedRouteElement>} />
            <Route path="/portfolio/weather-forecast-app" element={<AnimatedRouteElement><WeatherForecastApp /></AnimatedRouteElement>} />
            <Route
              path="*"
              element={
                <AnimatedRouteElement>
                  <section className="pt-32 pb-20 min-h-screen bg-primary text-center">
                    <h2 className="text-3xl font-bold text-primary mb-4">Page not found</h2>
                    <p className="text-secondary mb-8">That page doesn't exist (yet).</p>
                    <Link to="/" className="accent-bg text-white px-6 py-3 rounded-full font-semibold hover:opacity-90 transition">
                      Back home
                    </Link>
                  </section>
                </AnimatedRouteElement>
              }
            />
          </AnimatedRoutes>

          {/* Footer */}
          <footer className="bg-secondary text-primary py-8 border-t border-primary">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <p>&copy; {new Date().getFullYear()} Matthew Daggs. All rights reserved.</p>
              <div className="flex justify-center space-x-4 mt-4">
                <a href="/privacy-policy" className="hover:text-accent">Privacy Policy</a>
                <a href="/terms-of-service" className="hover:text-accent">Terms of Service</a>
              </div>
            </div>
          </footer>
        </div>
      </Router>
    </ThemeProvider>
  );
};

export default App;